import React, { useState, useEffect, useCallback, useRef } from "react";
import { getJson, postJson } from "../../services/http";
import "./DocGenDashboard.css";

const POLL_INTERVAL = 2000;

const STAGE_LABEL = {
  launch:   "Launching browser",
  login:    "Signing in",
  flow:     "Walking flow",
  write:    "Writing docs",
  finalize: "Building output",
  done:     "Complete",
};

const STAGE_ICON = {
  launch:   "bi-browser-chrome",
  login:    "bi-shield-check",
  flow:     "bi-signpost-split",
  write:    "bi-pencil-square",
  finalize: "bi-file-earmark-text",
  done:     "bi-check-circle-fill",
};

export default function DocGenDashboard({ tenantId }) {
  const [authorized, setAuthorized]   = useState(null);   // null = checking
  const [view, setView]               = useState("dash"); // dash | run | docs | detail
  const [health, setHealth]           = useState(null);
  const [healthLoading, setHL]        = useState(true);
  const [flows, setFlows]             = useState([]);
  const [selected, setSelected]       = useState(new Set());
  const [changeContext, setCtx]       = useState("");
  const [activeRun, setActiveRun]     = useState(null);
  const [runError, setRunError]       = useState(null);
  const [docs, setDocs]               = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const pollRef = useRef(null);

  // ── Authorize ──────────────────────────────────────────────────────────
  useEffect(() => {
    getJson("/api/super-admin/check-access").then(() => setAuthorized(true)).catch(() => setAuthorized(false));
  }, []);

  // ── Bootstrap once authorized ──────────────────────────────────────────
  const refreshHealth = useCallback(() => {
    setHL(true);
    getJson("/api/docgen/health")
      .then(setHealth)
      .catch((e) => setHealth({ healthy: false, checks: [], error: e.message }))
      .finally(() => setHL(false));
  }, []);

  const refreshDocs = useCallback(() => {
    getJson("/api/docgen/runs").then(d => setDocs(d.runs || []));
  }, []);

  useEffect(() => {
    if (!authorized) return;
    refreshHealth();
    getJson("/api/docgen/flows").then(d => {
      setFlows(d.flows || []);
      setSelected(new Set((d.flows || []).map(f => f.id)));
    });
    refreshDocs();
  }, [authorized, refreshHealth, refreshDocs]);

  // ── Polling for an active run ──────────────────────────────────────────
  const pollRun = useCallback((runId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = () => {
      getJson(`/api/docgen/runs/${runId}`).then(d => {
        setActiveRun(d.run);
        if (d.run.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          refreshDocs();
          if (d.run.status === "error") {
            setRunError({ title: "Run failed", message: d.run.error || "Unknown error." });
          }
        }
      }).catch(err => {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRunError({ title: "Lost connection", message: err.message });
      });
    };
    tick();
    pollRef.current = setInterval(tick, POLL_INTERVAL);
  }, [refreshDocs]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ── Actions ────────────────────────────────────────────────────────────
  const handleStart = async () => {
    const flowIds = [...selected];
    if (!flowIds.length) return;
    setRunError(null);
    try {
      const d = await postJson("/api/docgen/run", { flowIds, changeContext });
      if (d.runId) {
        setActiveRun({ id: d.runId, status: "running", flowIds, progress: [], startedAt: new Date().toISOString() });
        setView("run");
        pollRun(d.runId);
      }
    } catch (err) {
      setRunError({ title: "Failed to start", message: err.message });
    }
  };

  const toggleFlow = (id) => setSelected(p => {
    const n = new Set(p);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const selectAll = () => setSelected(new Set(flows.map(f => f.id)));
  const selectNone = () => setSelected(new Set());

  const openDoc = async (run) => {
    try {
      const d = await getJson(`/api/docgen/runs/${run.id}/full`);
      setSelectedDoc(d.doc);
      setView("detail");
    } catch (err) {
      setRunError({ title: "Cannot open doc", message: err.message });
    }
  };

  // ── Renders ────────────────────────────────────────────────────────────
  if (authorized === null) return <div className="dg-root"><div className="dg-loading">Checking access…</div></div>;
  if (!authorized) return (
    <div className="dg-root">
      <div className="dg-deny">
        <i className="bi bi-shield-lock" /> Super admin access required.
      </div>
    </div>
  );

  return (
    <div className="dg-root">
      <header className="dg-hdr">
        <div className="dg-brand">
          <i className="bi bi-file-earmark-text dg-bico" />
          <div>
            <h2>Documentation Generator</h2>
            <span className="dg-sub">Walk OnScreen with Playwright, write user docs with Claude</span>
          </div>
        </div>
        <div className="dg-nav">
          <button className={`dg-nbtn${view === "dash" ? " on" : ""}`} onClick={() => setView("dash")}>
            <i className="bi bi-play-circle" /> New run
          </button>
          <button className={`dg-nbtn${view === "docs" || view === "detail" ? " on" : ""}`} onClick={() => { setSelectedDoc(null); setView("docs"); refreshDocs(); }}>
            <i className="bi bi-files" /> Docs
            {docs.length > 0 && <span className="dg-badge">{docs.length}</span>}
          </button>
        </div>
      </header>

      {/* Run error banner */}
      {runError && (
        <div className="dg-err">
          <strong>{runError.title}</strong>
          <div>{runError.message}</div>
          <button onClick={() => setRunError(null)}>Dismiss</button>
        </div>
      )}

      {/* Dashboard view */}
      {view === "dash" && (
        <DashView
          flows={flows} selected={selected} toggleFlow={toggleFlow}
          selectAll={selectAll} selectNone={selectNone}
          health={health} healthLoading={healthLoading} refreshHealth={refreshHealth}
          changeContext={changeContext} setCtx={setCtx}
          onStart={handleStart}
        />
      )}

      {/* Run view */}
      {view === "run" && activeRun && (
        <RunView
          run={activeRun}
          onView={() => { refreshDocs(); openDoc(activeRun); }}
          onBack={() => setView("dash")}
        />
      )}

      {/* Docs list */}
      {view === "docs" && (
        <DocsList docs={docs} onSelect={openDoc} onNewRun={() => setView("dash")} />
      )}

      {/* Doc detail */}
      {view === "detail" && selectedDoc && (
        <DocDetail doc={selectedDoc} onBack={() => setView("docs")} />
      )}
    </div>
  );
}

/* ─── Dashboard sub-views ─── */

function DashView({ flows, selected, toggleFlow, selectAll, selectNone, health, healthLoading, refreshHealth, changeContext, setCtx, onStart }) {
  const blockers = health?.checks?.filter(c => !c.ok && c.id !== 'anthropic') || [];
  const warnings = health?.checks?.filter(c => !c.ok && c.id === 'anthropic') || [];
  const canRun = selected.size > 0 && blockers.length === 0;

  return (
    <div className="dg-body">
      {/* Health */}
      <section className="dg-card">
        <div className="dg-card-hdr">
          <span>Environment</span>
          <button className="dg-mini" onClick={refreshHealth} disabled={healthLoading}>
            {healthLoading ? "Checking…" : "Refresh"}
          </button>
        </div>
        <div className="dg-card-body">
          {healthLoading && !health && <div className="dg-sub">Checking…</div>}
          {health?.checks?.map(c => (
            <div key={c.id} className={`dg-check ${c.ok ? "ok" : c.id === "anthropic" ? "warn" : "bad"}`}>
              <i className={`bi ${c.ok ? "bi-check-circle-fill" : c.id === "anthropic" ? "bi-exclamation-circle" : "bi-x-circle-fill"}`} />
              <div>
                <div className="dg-check-label">{c.label}</div>
                {!c.ok && c.detail && <div className="dg-check-detail">{c.detail}</div>}
              </div>
            </div>
          ))}
          {warnings.length > 0 && blockers.length === 0 && (
            <div className="dg-warn">
              Documentation will use a deterministic stub for each section. Set ANTHROPIC_API_KEY for Claude-written prose.
            </div>
          )}
        </div>
      </section>

      {/* Change context */}
      <section className="dg-card">
        <div className="dg-card-hdr"><span>What changed (optional)</span></div>
        <div className="dg-card-body">
          <textarea
            className="dg-textarea"
            rows={3}
            placeholder="e.g. New 3-step checkout wizard. Settings page added notification prefs."
            value={changeContext}
            onChange={e => setCtx(e.target.value)}
          />
          <div className="dg-sub">Sent to Claude alongside each section so descriptions reflect recent updates.</div>
        </div>
      </section>

      {/* Flows */}
      <section className="dg-card">
        <div className="dg-card-hdr">
          <span>Flows to document</span>
          <span>
            <button className="dg-mini" onClick={selectAll}>All</button>
            <button className="dg-mini" onClick={selectNone}>None</button>
          </span>
        </div>
        <div className="dg-card-body">
          <div className="dg-flow-grid">
            {flows.map(f => (
              <button
                key={f.id}
                className={`dg-flow${selected.has(f.id) ? " sel" : ""}`}
                onClick={() => toggleFlow(f.id)}
              >
                <div className="dg-flow-top">
                  <input type="checkbox" checked={selected.has(f.id)} readOnly />
                  <span className="dg-flow-title">{f.title}</span>
                </div>
                <span className="dg-flow-purpose">{f.purpose}</span>
                <span className="dg-flow-meta">{f.screenshotCount} screenshot{f.screenshotCount !== 1 ? "s" : ""}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <button className={`dg-start${canRun ? "" : " off"}`} disabled={!canRun} onClick={onStart}>
        <i className="bi bi-play-fill" /> Generate documentation
      </button>
      {blockers.length > 0 && (
        <div className="dg-warn">Fix the environment issues above before running.</div>
      )}
    </div>
  );
}

function RunView({ run, onView, onBack }) {
  const isDone    = run.status === "done";
  const isError   = run.status === "error";
  const isRunning = run.status === "running";

  // Group progress by flow id (most recent message wins)
  const flowProgress = {};
  for (const p of run.progress || []) {
    if (p.flowId) flowProgress[p.flowId] = p;
  }
  const headline = run.progress?.length ? run.progress[run.progress.length - 1] : null;

  return (
    <div className="dg-body">
      <section className="dg-card">
        <div className="dg-card-hdr">
          <span>{isRunning ? "Running…" : isError ? "Failed" : "Done"}</span>
          <button className="dg-mini" onClick={onBack}>← Back</button>
        </div>
        <div className="dg-card-body">
          {isRunning && (
            <div className="dg-status">
              <span className="dg-spin" />
              <span>{headline ? `${STAGE_LABEL[headline.stage] || headline.stage}: ${headline.message}` : "Starting…"}</span>
            </div>
          )}
          {isDone && (
            <div className="dg-done">
              <i className="bi bi-check-circle-fill" /> Documentation generated.
              <button className="dg-cta" onClick={onView}>Open</button>
            </div>
          )}
          {isError && (
            <pre className="dg-err-pre">{run.error}</pre>
          )}

          <div className="dg-progress-log">
            {(run.progress || []).slice(-30).map((p, i) => (
              <div key={i} className={`dg-log-row ${p.stage}`}>
                <i className={`bi ${STAGE_ICON[p.stage] || "bi-circle"}`} />
                <span className="dg-log-stage">{STAGE_LABEL[p.stage] || p.stage}</span>
                <span className="dg-log-msg">{p.message}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function DocsList({ docs, onSelect, onNewRun }) {
  if (!docs.length) {
    return (
      <div className="dg-body">
        <div className="dg-empty">
          <i className="bi bi-files" />
          <h3>No documentation yet</h3>
          <p>Run DocGen against this tenant to generate the first set of user docs.</p>
          <button className="dg-cta" onClick={onNewRun}><i className="bi bi-play-fill" /> Start first run</button>
        </div>
      </div>
    );
  }
  return (
    <div className="dg-body">
      <section className="dg-card">
        <div className="dg-card-hdr">
          <span>Past runs</span>
          <button className="dg-mini" onClick={onNewRun}><i className="bi bi-plus" /> New run</button>
        </div>
        <div className="dg-card-body">
          <div className="dg-runs">
            {docs.map(d => (
              <button key={d.id} className="dg-run" onClick={() => onSelect(d)} disabled={d.status === "running"}>
                <div className="dg-run-meta">
                  <span className="dg-run-id">{d.id}</span>
                  <span className="dg-run-when">{d.startedAt ? new Date(d.startedAt).toLocaleString() : ""}</span>
                </div>
                <div className="dg-run-stats">
                  <span><i className="bi bi-signpost-split" /> {d.flowCount} flow{d.flowCount !== 1 ? "s" : ""}</span>
                  <span><i className="bi bi-camera" /> {d.totalScreenshots} shots</span>
                  <span className={`dg-pill ${d.status}`}>{d.status}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function DocDetail({ doc, onBack }) {
  const [active, setActive] = useState(null);
  const visible = active ? doc.flows.filter(f => f.id === active) : doc.flows;

  const exportMd = () => {
    const md = doc.flows.map(f => {
      const shots = f.screenshots.map(s => `![${s.label}](${s.url})`).join('\n\n');
      return `## ${f.title}\n\n${f.body}\n\n${shots}`;
    }).join('\n\n---\n\n');
    const full = `# OnScreen — User documentation\n\n_Generated ${new Date(doc.generatedAt).toLocaleString()} from ${doc.targetUrl}_\n\n${md}`;
    const blob = new Blob([full], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `docgen-${doc.id}.md`;
    a.click();
  };

  return (
    <div className="dg-body">
      <div className="dg-detail-hdr">
        <button className="dg-mini" onClick={onBack}>← All docs</button>
        <div className="dg-detail-meta">
          <strong>{doc.targetUrl}</strong>
          <span>{new Date(doc.generatedAt).toLocaleString()} · {doc.totalScreenshots} screenshots</span>
        </div>
        <div className="dg-detail-actions">
          {doc.markdownUrl && (
            <a className="dg-mini" href={doc.markdownUrl} target="_blank" rel="noopener noreferrer">
              <i className="bi bi-box-arrow-up-right" /> Raw .md
            </a>
          )}
          <button className="dg-cta" onClick={exportMd}><i className="bi bi-download" /> Export</button>
        </div>
      </div>

      <div className="dg-detail-grid">
        <aside className="dg-side">
          <button className={`dg-side-item${!active ? " on" : ""}`} onClick={() => setActive(null)}>
            <i className="bi bi-layout-text-window" /> All sections
          </button>
          {doc.flows.map(f => (
            <button key={f.id} className={`dg-side-item${active === f.id ? " on" : ""}`} onClick={() => setActive(f.id)}>
              <i className="bi bi-file-text" /> {f.title}
            </button>
          ))}
        </aside>
        <div className="dg-detail-body">
          {visible.map(f => (
            <article key={f.id} className="dg-section">
              <h3>{f.title}</h3>
              <Markdown text={f.body} />
              {f.screenshots.length > 0 && (
                <div className="dg-shots">
                  {f.screenshots.map((s, i) => (
                    <figure key={i} className="dg-shot">
                      <img src={s.url} alt={s.label} loading="lazy" />
                      <figcaption>{s.label}</figcaption>
                    </figure>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Lightweight markdown renderer — handles numbered steps, tips, paragraphs */
function Markdown({ text }) {
  const lines = (text || "").split("\n");
  return (
    <div className="dg-md">
      {lines.map((l, i) => {
        if (!l.trim()) return null;
        const num = l.match(/^(\d+)\.\s+(.*)/);
        if (num) return (
          <div key={i} className="dg-step">
            <span className="dg-step-n">{num[1]}</span>
            <span>{num[2]}</span>
          </div>
        );
        if (/^tip:/i.test(l.trim())) return (
          <div key={i} className="dg-tip"><i className="bi bi-lightbulb-fill" /> {l.replace(/^tip:\s*/i, '')}</div>
        );
        return <p key={i} className="dg-p">{l}</p>;
      })}
    </div>
  );
}
